import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import PropTypes from 'prop-types';
import {
  Modal,
  Button,
  Typography,
  Divider,
  InputNumber,
  Row,
  Col,
  Select,
  Space
} from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import numeral from 'numeral';
import { CheckCircleOutlined } from '@ant-design/icons';
import useQuotesContext from './QuotesContext';
import { formatValue } from '../../utils/formatValue';
// eslint-disable-next-line import/no-cycle
import useDefaultValues from './defaultValues';
import frenchFlag from '../../assets/images/french-flag.svg';
import maxMedal from '../../assets/images/max-medal.svg';
import {
  inputNumberFormatter,
  inputNumberParser
} from '../../utils/inputNumberFormatter';
import WithRibbon from '../../components/WithRibbon/WithRibbon';
import LogoContainer from '../../components/LogoContainer/LogoContainer';

import MilaLogo from '../../assets/images/logos/mila.png';
import WakamLogo from '../../assets/images/logos/wakam.svg';
import HiscoxLogo from '../../assets/images/logos/hiscox.svg';

import RCP_ACTIVITIES_PDF from '../../assets/rcp_activities_list.pdf';
import MRP_ACTIVITIES_PDF from '../../assets/mrp_activities_list.pdf';

const ChoiceOfferModal = ({
  openChoiceOfferModal,
  setOpenChoiceOfferModal,
  productSelected
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    codesNaf = [],
    activitiesHiscox = [],
    getEstimationLazy,
    setDataModal,
    setEstimatedCost
  } = useQuotesContext();
  const [activity, setActivity] = useState();
  const [existingManySuppliers, setExistingManySuppliers] = useState(false);
  const [hiscoxActivity, setHiscoxActivity] = useState(false);
  const [displayPrice, setDisplayPrice] = useState(true);
  const [surfaceArea, setSurfaceArea] = useState();
  const [turnover, setTurnover] = useState();
  const [offerWK, setOfferWK] = useState();
  const [offerML, setOfferML] = useState();
  const [offerHX, setOfferHX] = useState();
  const [offersLoading, setOffersLoading] = useState(false);
  const [offersInitialized, setOffersInitialized] = useState(false);
  const [highlightWK, setHighlightWK] = useState(false);

  const [fireExlosionAttackClimaticEvent, setFireExlosionAttackClimaticEvent] =
    useState(10000);

  const mrpT = productSelected === 'MRP' ? 'any' : 'office';

  let defaultValuesMRP = useDefaultValues({
    type: 'mrp',
    mrpType: mrpT,
    assoType: 'localRC'
  });

  let defaultValuesMilaMRP = useDefaultValues({
    type: 'mrp',
    mrpType: mrpT,
    assoType: 'localRC',
    supplier: 'ml'
  });

  let defaultValuesRCP = useDefaultValues({
    type: 'rcp',
    mrpType: mrpT,
    assoType: 'localRC',
    supplier: 'wk'
  });

  let defaultValuesMilaRCP = useDefaultValues({
    type: 'rcp',
    mrpType: mrpT,
    assoType: 'localRC',
    supplier: 'ml'
  });

  const defaultValuesRCPHX = useDefaultValues({
    type: 'rcphx'
  });

  const optionsRCPHX = [
    'legal_protection_usa_canada',
    'legal_protection_rcphx',
    'exploitation_responsibility'
  ];

  const frenchSuppliers = ['MILA', 'WAKAM'];
  const activitiesRCPHX = [
    { type: 'TECH', label: 'TECH' },
    { type: 'MAC', label: 'MAC' },
    { type: 'MAN', label: 'MAN' },
    { type: 'MISC', label: 'MISC' },
    { type: 'BIE', label: 'BIE' },
    { type: 'MEDIA', label: 'MEDIA' },
    { type: 'TAG', label: 'TAG' },
    { type: 'SECU', label: 'SECU' },
    { type: 'OSTEO', label: 'OSTEO' },
    { type: 'MED', label: 'MED' }
  ];

  const checkIfExemption = () => {
    if (
      turnover > 500000 ||
      surfaceArea > 400 ||
      fireExlosionAttackClimaticEvent > 100000
    ) {
      return true;
    }
    return false;
  };

  const checkIfAvailable = (supplier) => {
    if (activity?.length) {
      const activityDetails = codesNaf.find(
        (code) => code.name.trim() === activity.split('- ')[0].trim()
      );
      const suppliers = activityDetails?.display_modal?.[productSelected];
      setDisplayPrice(
        !checkIfExemption() && !activityDetails?.derogatory_activity
      );
      return suppliers?.includes(supplier);
    }
    return false;
  };

  const activities = useMemo(() => {
    const list = codesNaf
      .filter(
        (code) =>
          code?.display_modal?.[productSelected]?.includes('WAKAM') ||
          code?.display_modal?.[productSelected]?.includes('MILA')
      )
      .map(({ naf_code, name }) => ({
        value: `${name} - ${naf_code || 'N/A'}`,
        label: `${name} - ${naf_code || 'Naf non renseigné'}`
      }));

    if (productSelected?.includes('RCP')) {
      const listHiscox = activitiesHiscox.map((a) => ({
        value: a.name,
        label: a.name,
        hiscox: true
      }));

      const combinedList = [...list, ...listHiscox];
      const uniqueList = combinedList.reduce((acc, item) => {
        const itemKey = item.label.split(' - ')[0].trim();

        const existing = acc.find(
          (entry) => entry.label.split(' - ')[0].trim() === itemKey
        );

        if (existing) {
          existing.hiscox = existing.hiscox || item.hiscox;
          existing.manySuppliers = true;
        } else {
          acc.push({ ...item });
        }
        return acc;
      }, []);
      return uniqueList;
    }

    return list;
  }, [productSelected, codesNaf, activitiesHiscox]);

  const offers = useMemo(() => {
    if (!offersInitialized) {
      return [];
    }

    if (productSelected?.includes('MRP')) {
      return [offerWK, offerML];
    }
    if (productSelected?.includes('RCP')) {
      if (existingManySuppliers) {
        return [offerML, offerHX];
      }
      if (hiscoxActivity) {
        return [offerHX];
      }
      return [offerWK, offerML];
    }

    return [];
  }, [offersLoading, offerML, offerWK, offerHX]);

  // eslint-disable-next-line consistent-return
  const goToCreate = (shortcut, price, type = null) => {
    setEstimatedCost(price);
    if (productSelected?.includes('MRP')) {
      setDataModal({
        ...(type === 'MRPML' ? defaultValuesMilaMRP : defaultValuesMRP),
        ...(productSelected !== 'MRPB' ? { activities: [activity] } : {}),
        PACKAGE_1: {
          ...defaultValuesMRP.PACKAGE_1,
          ...(type === 'MRPML'
            ? defaultValuesMilaMRP.PACKAGE_1
            : defaultValuesMRP.PACKAGE_1),
          fire_explosion_attack_climatic_event: fireExlosionAttackClimaticEvent
        },
        sites: [
          {
            surface_area: surfaceArea,
            tenant_status: 'WITHOUT_CLAUSE',
            protection_level: 1,
            resident_status: 'TENANT'
          }
        ]
      });
    } else if (type === 'RCPHX') {
      const selectedActivity = activitiesHiscox.find(
        (a) => a.name === activity.split(' - ')[0].trim()
      );
      setDataModal({
        ...defaultValuesRCPHX,
        activity_type: selectedActivity?.type || 'MISC',
        activity_detail: selectedActivity ? [selectedActivity.name] : [],
        turnover
      });

      return navigate('/quotes/rcphx/create');
    } else {
      setDataModal({
        ...(type === 'RCPML' ? defaultValuesMilaRCP : defaultValuesRCP),
        activities: [activity],
        turnover,
        // eslint-disable-next-line no-nested-ternary
        guarantee_limit: type === 'RCPML' ? 50000 : shortcut ? 150000 : 100000
      });
    }
    const link = `/quotes/${productSelected?.toLowerCase().slice(0, 3)}/create${
      productSelected?.includes('MRP') ? `?mrpT=${mrpT}&` : '?'
    }${shortcut ? `sup=${shortcut}` : ''}`;
    navigate(link);
  };

  const goToHX = (a) => {
    const link = `/quotes/rcphx/create`;
    setDataModal({
      ...defaultValuesRCPHX,
      activity_type: a,
      turnover: turnover ?? 0,
      guarantee_limit: a === 'TAG' ? 1_500_000 : undefined
    });
    navigate(link);
  };

  const updateOfferML = async (defaultValues, costComparison) => {
    const costML = (await getEstimationLazy(defaultValues, 'RCPML')) || 0;
    setOfferML({
      label: 'MILASSUR’RCPRO',
      rate: costML,
      available: checkIfAvailable('MILA'),
      lawsuit: false,
      better: costML <= costComparison,
      shortcut: 'ml',
      supplier: 'MILA',
      type: 'RCPML'
    });
    return costML;
  };

  const getRateBySupplier = async () => {
    try {
      setOffersLoading(true);
      if (productSelected?.includes('MRP')) {
        if (
          surfaceArea &&
          fireExlosionAttackClimaticEvent &&
          (activity || productSelected === 'MRPB')
        ) {
          defaultValuesMRP = {
            ...defaultValuesMRP,
            ...(productSelected === 'MRP' ? [{ activities: [activity] }] : []),
            PACKAGE_1: {
              fire_explosion_attack_climatic_event:
                fireExlosionAttackClimaticEvent
            },
            sites: [
              {
                surface_area: surfaceArea,
                tenant_status: 'WITHOUT_CLAUSE',
                protection_level: 1,
                resident_status: 'TENANT'
              }
            ]
          };
          defaultValuesMilaMRP = {
            ...defaultValuesMilaMRP,
            ...(productSelected === 'MRP' ? [{ activities: [activity] }] : []),
            PACKAGE_1: {
              fire_explosion_attack_climatic_event:
                fireExlosionAttackClimaticEvent
            },
            sites: [
              {
                surface_area: surfaceArea,
                tenant_status: 'WITHOUT_CLAUSE',
                protection_level: 1,
                resident_status: 'TENANT'
              }
            ]
          };
          const costWK =
            (await getEstimationLazy(defaultValuesMRP, 'MRPWK')) || 0;
          const costML =
            (await getEstimationLazy(defaultValuesMilaMRP, 'MRPML')) || 0;
          const costSM =
            (await getEstimationLazy(defaultValuesMRP, 'MRP')) || 0;
          setOfferML({
            label: 'MILASSUR’MRPRO ',
            rate: costML,
            better: costML <= costWK && costML <= costSM,
            available:
              productSelected === 'MRP' ? checkIfAvailable('MILA') : true,
            shortcut: 'ml',
            lawsuit: false,
            supplier: 'MILA',
            type: 'MRPML'
          });
          setOfferWK({
            label: 'MR PRO ASSUR2',
            rate: costWK,
            better: costWK < costML && costWK < costSM,
            available:
              productSelected === 'MRP' ? checkIfAvailable('WAKAM') : true,
            shortcut: 'wk',
            lawsuit: true,
            supplier: 'WAKAM',
            type: 'MRPWK'
          });
          setHighlightWK(
            productSelected === 'MRP' ? checkIfAvailable('WAKAM') : true
          );
        }
      } else if (activity && turnover) {
        defaultValuesRCP = {
          ...defaultValuesRCP,
          activities: [activity],
          turnover
        };
        defaultValuesMilaRCP = {
          ...defaultValuesMilaRCP,
          activities: [activity],
          turnover
        };

        if (hiscoxActivity) {
          const selectedActivity = activitiesHiscox.find(
            (a) => a.name === activity
          );

          const costHX =
            (await getEstimationLazy(
              {
                ...defaultValuesRCPHX,
                activity_type: selectedActivity?.type || 'MISC',
                activity_detail: selectedActivity
                  ? [selectedActivity.name]
                  : [],
                turnover
              },
              'RCPHX'
            )) || 0;
          let costML = 0;
          if (existingManySuppliers) {
            costML = await updateOfferML(defaultValuesMilaRCP, costHX);
          }
          setOfferHX({
            label: 'RC PRO NOUVELLE OFFRE',
            rate: costHX,
            available: hiscoxActivity,
            better: costHX < costML,
            shortcut: undefined,
            supplier: 'HISCOX',
            type: 'RCPHX'
          });
        } else {
          const available = checkIfAvailable('WAKAM');
          const costWK =
            (await getEstimationLazy(defaultValuesRCP, 'RCPWK')) || 0;
          const costML = await updateOfferML(defaultValuesMilaRCP, costWK);
          setOfferWK({
            label: 'RC PRO ASSUR2',
            rate: costWK,
            lawsuit: true,
            available,
            better: costWK < costML,
            shortcut: 'wk',
            supplier: 'WAKAM',
            type: 'RCPWK'
          });
          setHighlightWK(available);
        }
      }
      // eslint-disable-next-line no-empty
    } catch (error) {
    } finally {
      setOffersLoading(false);
      setOffersInitialized(true);
    }
  };

  const getOfferStatus = (available, better) => {
    if (available) {
      return better ? 'better_offer' : 'other_offer';
    }
    return 'not_available';
  };

  const getWidthCol = (i) => {
    if (offers?.length === 1) return 8;

    if (highlightWK) {
      if (i === 1) return 9;
      return 6;
    }
    return 7;
  };

  useEffect(() => {
    getRateBySupplier();
  }, [
    surfaceArea,
    turnover,
    fireExlosionAttackClimaticEvent,
    activity,
    activity?.lenght
  ]);

  useEffect(() => {
    if (openChoiceOfferModal) {
      setActivity(undefined);
      setSurfaceArea(undefined);
      setTurnover(undefined);
      setOfferWK(undefined);
      setOfferML(undefined);
      setOfferHX(undefined);
      setOffersInitialized(false);
      setFireExlosionAttackClimaticEvent(10000);
    }
  }, [openChoiceOfferModal]);

  return (
    <Modal
      title={
        <>
          <img
            src={maxMedal}
            alt="max_medal"
            style={{
              height: '49.5px',
              position: 'absolute',
              left: '1.5%',
              top: '0.5%'
            }}
          />
          {productSelected &&
            t(`choice_offer_modal.title_${productSelected.toLowerCase()}`)}
        </>
      }
      styles={{
        body: {
          position: 'relative',
          padding: '0'
        }
      }}
      centered
      open={openChoiceOfferModal}
      footer={false}
      onCancel={() => setOpenChoiceOfferModal(false)}
      width={1200}
    >
      <WithRibbon
        text={t('choice_offer_modal.ribbon.best_seller')}
        display={productSelected === 'RCP'}
      >
        <Col style={{ padding: '4px 0' }}>
          <Col>
            <Space size="small" style={{ padding: '0 4px' }}>
              <LogoContainer src={MilaLogo} />
              <LogoContainer src={WakamLogo} />
              {productSelected?.includes('RCP') && (
                <LogoContainer src={HiscoxLogo} />
              )}
            </Space>
            <Row style={{ paddingBottom: 10 }}>
              {productSelected !== 'MRPB' && (
                <>
                  <Col span={12}>
                    <Row style={{ justifyContent: 'center' }}>
                      <span>{t('choice_offer_modal.main_activity')}</span>
                    </Row>
                    <Row style={{ justifyContent: 'center' }}>
                      <Select
                        className="activities-select"
                        style={{ width: 400, maxWidth: 400 }}
                        value={activity}
                        onChange={(value, option) => {
                          setActivity(value);
                          setExistingManySuppliers(option?.manySuppliers);
                          if (option?.hiscox) {
                            setHiscoxActivity(true);
                          } else {
                            setHiscoxActivity(false);
                          }
                        }}
                        options={activities}
                        filterOption={(input, option) =>
                          formatValue(option.label).indexOf(
                            formatValue(input)
                          ) > -1
                        }
                        showSearch
                      />
                    </Row>
                    <Row justify="center" align="middle">
                      <Typography.Link
                        href={
                          productSelected?.includes('RCP')
                            ? RCP_ACTIVITIES_PDF
                            : MRP_ACTIVITIES_PDF
                        }
                        download={
                          productSelected?.includes('RCP')
                            ? 'rcp-activities.pdf'
                            : 'mrp-activities.pdf'
                        }
                        style={{ textTransform: 'none' }}
                        underline
                      >
                        {t('choice_offer_modal.download_activities_list')}
                      </Typography.Link>
                    </Row>
                  </Col>
                </>
              )}
              {productSelected?.includes('MRP') ? (
                <>
                  <Col span={productSelected === 'MRP' ? 5 : 12}>
                    <Row style={{ justifyContent: 'center' }}>
                      <span>
                        {t('choice_offer_modal.surface')}
                        <sup style={{ fontSize: '10px' }}> (m2)</sup>
                      </span>
                    </Row>
                    <Row style={{ justifyContent: 'center' }}>
                      <InputNumber
                        value={surfaceArea}
                        onChange={(value) => setSurfaceArea(value)}
                        min={0}
                        placeholder={50}
                        style={{ width: 120 }}
                      />
                    </Row>
                  </Col>
                  <Col span={productSelected === 'MRP' ? 5 : 12}>
                    <Row style={{ justifyContent: 'center' }}>
                      <span>
                        {t('choice_offer_modal.fire')}
                        <sup style={{ fontSize: '10px' }}> (€)</sup>
                      </span>
                    </Row>
                    <Row style={{ justifyContent: 'center' }}>
                      <InputNumber
                        value={fireExlosionAttackClimaticEvent}
                        onChange={(value) =>
                          value <= 800000 &&
                          setFireExlosionAttackClimaticEvent(value)
                        }
                        max={800000}
                        min={0}
                        style={{ width: 120 }}
                      />
                    </Row>
                  </Col>
                </>
              ) : (
                <Col span={10}>
                  <Row style={{ justifyContent: 'center' }}>
                    <span>
                      {t('choice_offer_modal.turnover')}
                      <sup style={{ fontSize: '10px' }}> (€)</sup>
                    </span>
                  </Row>
                  <Row style={{ justifyContent: 'center' }}>
                    <InputNumber
                      value={turnover}
                      onChange={(value) =>
                        value <= 2000000 && setTurnover(value)
                      }
                      min={0}
                      max={2000000}
                      placeholder={200000}
                      style={{ width: 120 }}
                      addonAfter="€"
                      step={10000}
                      formatter={inputNumberFormatter}
                      parser={inputNumberParser}
                    />
                  </Row>
                </Col>
              )}
            </Row>
          </Col>
          {offersInitialized && offers.length > 0 && <Divider />}
          <Row justify="center" gutter={10}>
            {offersInitialized &&
              offers.map((offer, index) => (
                <Col
                  span={getWidthCol(index)}
                  key={(offer, index)}
                  style={{
                    borderRadius: '6px',
                    marginLeft: index > 0 ? '0.5%' : '0',
                    marginRight: index < 2 ? '0.5%' : '0%',
                    border: '2px solid',
                    borderColor:
                      offer?.better && offer?.available && displayPrice
                        ? '#0C5787'
                        : 'transparent',
                    boxShadow: '0 2px 2px rgb(0 0 0 / 20%)'
                  }}
                >
                  <Row style={{ justifyContent: 'center' }}>
                    <Typography.Text
                      level={1}
                      style={{ fontSize: '17px' }}
                      underline
                    >
                      {offer?.label}
                    </Typography.Text>
                    <span
                      style={{
                        fontSize: 9,
                        right: frenchSuppliers.includes(offer?.supplier)
                          ? 20
                          : 5,
                        position: 'absolute',
                        top: '2%'
                      }}
                    >
                      {offer?.supplier}
                    </span>
                    {frenchSuppliers.includes(offer?.supplier) && (
                      <img
                        src={frenchFlag}
                        alt="french_flag"
                        style={{
                          height: 15,
                          right: 0,
                          position: 'absolute',
                          top: '1.5%'
                        }}
                      />
                    )}
                  </Row>
                  {(displayPrice || !offer?.available) && (
                    <Row style={{ justifyContent: 'center' }}>
                      <Typography.Text
                        italic
                        level={5}
                        style={{ fontSize: '10px' }}
                      >
                        {t(
                          `choice_offer_modal.${getOfferStatus(
                            offer?.available,
                            offer?.better
                          )}`
                        )}
                      </Typography.Text>
                    </Row>
                  )}
                  {displayPrice && offer?.available ? (
                    <>
                      <Row style={{ justifyContent: 'center' }}>
                        {t('choice_offer_modal.base_price')}
                      </Row>
                      <Row
                        style={{
                          justifyContent: 'center',
                          fontSize: '18px'
                        }}
                      >
                        <span
                          style={{
                            borderBottom: '1px solid',
                            borderRadius: '1px',
                            padding: '1%',
                            borderColor:
                              offer?.better && offer?.available && displayPrice
                                ? 'gold'
                                : 'lightgray',
                            boxShadow:
                              offer?.better && offer?.available && displayPrice
                                ? '0 4px 2px -2px gold'
                                : '0 4px 2px -2px lightgray'
                          }}
                        >
                          {numeral(offer?.rate).format('0,0.00$')}
                          &nbsp;
                          <span>{t('choice_offer_modal.per_month')}</span>
                          <sup>*</sup>
                        </span>
                      </Row>
                    </>
                  ) : (
                    <>
                      <Row
                        style={{
                          justifyContent: 'center',
                          fontSize: '18px'
                        }}
                      >
                        <span
                          style={{
                            borderBottom: '1px solid',
                            borderRadius: '1px',
                            padding: '1%',
                            borderColor:
                              offer?.available && offer?.better
                                ? 'gold'
                                : 'lightgray',
                            boxShadow:
                              offer?.available && offer?.better
                                ? '0 2px 2px -2px gold'
                                : '0 2px 2px -2px lightgray'
                          }}
                        >
                          <span>
                            {offer?.available &&
                              (offer?.better
                                ? t('choice_offer_modal.better_price')
                                : t('choice_offer_modal.custom_offer'))}
                          </span>
                        </span>
                      </Row>
                    </>
                  )}
                  {offer?.available && displayPrice && offer?.lawsuit && (
                    <>
                      <Row
                        style={{
                          justifyContent: 'center',
                          alignItems: 'center',
                          fontSize: index === 1 ? 14 : 12
                        }}
                      >
                        <CheckCircleOutlined
                          style={{ color: 'green', marginRight: '0.5%' }}
                        />
                        {t('choice_offer_modal.lawsuit')}
                      </Row>
                    </>
                  )}
                  <Row style={{ justifyContent: 'center', marginTop: '4%' }}>
                    <Button
                      type="primary"
                      style={{
                        position: !offer?.available ? 'absolute' : '',
                        bottom: '5px',
                        width: '90%',
                        color:
                          offer?.better && offer?.available
                            ? 'gold'
                            : 'lightgray'
                      }}
                      disabled={!offer?.available}
                      onClick={() =>
                        goToCreate(offer?.shortcut, offer?.rate, offer?.type)
                      }
                    >
                      <span>{t('choice_offer_modal.create_quote')}</span>
                    </Button>
                  </Row>
                </Col>
              ))}
            {offersInitialized && (
              <span
                style={{
                  position: 'absolute',
                  right: '1%',
                  bottom: '1%',
                  fontSize: '9px'
                }}
              >
                <sup>*</sup>
                {t('choice_offer_modal.without_fee')}
              </span>
            )}
          </Row>
          {productSelected?.includes('RCP') && (
            <>
              <Divider
                style={{
                  marginBottom: 0,
                  borderTopColor: 'var(--clientColor)'
                }}
              />
              <WithRibbon text={t('choice_offer_modal.ribbon.news')}>
                <Col style={{ padding: 24 }}>
                  <div style={{ position: 'absolute', top: 4, left: 4 }}>
                    <LogoContainer src={HiscoxLogo} />
                  </div>
                  <Row
                    justify="center"
                    align="middle"
                    gutter={50}
                    style={{ marginBottom: '2%' }}
                  >
                    <Typography.Text
                      level={1}
                      style={{ fontSize: '17px', marginBottom: '2%' }}
                    >
                      {t('choice_offer_modal.new_offers')}
                    </Typography.Text>
                  </Row>
                  <Row justify="center" gutter={10}>
                    <Col
                      span={11}
                      style={{
                        borderRadius: '6px',
                        marginLeft: '0.5%',
                        marginRight: '0.5%',
                        border: '2px solid',
                        borderColor: 'transparent',
                        boxShadow: '0 2px 2px rgb(0 0 0 / 20%)'
                      }}
                    >
                      <Row style={{ justifyContent: 'center' }}>
                        <Typography.Text
                          level={1}
                          style={{ fontSize: '15px' }}
                          underline
                        >
                          {t('choice_offer_modal.new_rc')}
                        </Typography.Text>
                      </Row>
                      <Row
                        wrap={false}
                        gutter={8}
                        style={{ marginTop: 8, marginBottom: 12 }}
                      >
                        <Col flex="none">
                          <Typography.Text
                            italic
                            style={{
                              fontSize: '11px',
                              display: 'block',
                              marginBottom: 6
                            }}
                          >
                            {t('choice_offer_modal.new_rc_content')}
                          </Typography.Text>
                          {optionsRCPHX.map((option) => (
                            <Row
                              key={option}
                              wrap={false}
                              style={{ fontSize: 12, marginTop: 4 }}
                            >
                              <Col>
                                <CheckCircleOutlined
                                  style={{ color: 'green', marginRight: 5 }}
                                />
                              </Col>
                              <Col>
                                <Trans>
                                  {t(`choice_offer_modal.options.${option}`, {
                                    defaultValue: t(`contracts.form.${option}`)
                                  })}
                                </Trans>
                              </Col>
                            </Row>
                          ))}
                        </Col>
                      </Row>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
                          gap: '6px'
                        }}
                      >
                        {activitiesRCPHX.map((a) => (
                          <Button
                            key={a.label}
                            onClick={() => goToHX(a.type)}
                            style={{
                              width: '100%',
                              minWidth: 0,
                              height: 'auto',
                              minHeight: '48px',
                              padding: '8px 4px',
                              fontSize: 11,
                              fontWeight: 500,
                              whiteSpace: 'normal',
                              overflowWrap: 'break-word',
                              lineHeight: 1.3,
                              borderRadius: 6,
                              background: '#f8fafc',
                              border: '1px solid #cbd5e1',
                              color: '#1e3a5f',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                            }}
                          >
                            <Trans>
                              {t(
                                `choice_offer_modal.options.rcphx.activity_type.${a.label}`,
                                {
                                  defaultValue: t(
                                    `contracts.rcphx.activity_type.${a.type}`
                                  )
                                }
                              )}
                            </Trans>
                          </Button>
                        ))}
                      </div>
                    </Col>
                    <Col
                      span={11}
                      style={{
                        borderRadius: '6px',
                        marginLeft: '0.5%',
                        marginRight: '0.5%',
                        border: '2px solid',
                        borderColor: 'transparent',
                        boxShadow: '0 2px 2px rgb(0 0 0 / 20%)'
                      }}
                    >
                      <Typography.Text
                        level={1}
                        style={{ fontSize: '13px' }}
                        underline
                      >
                        {t(
                          'choice_offer_modal.new_repository_activity_hx_title'
                        )}
                      </Typography.Text>
                      <p style={{ textAlign: 'justify' }}>
                        {t(
                          'choice_offer_modal.new_repository_activity_hx_content_first'
                        )}
                      </p>
                      <p style={{ textAlign: 'justify' }}>
                        <Trans
                          i18nKey="choice_offer_modal.new_repository_activity_hx_content_second"
                          components={[
                            <Typography.Link
                              download
                              href={RCP_ACTIVITIES_PDF}
                              style={{
                                textTransform: 'none',
                                display: 'inline'
                              }}
                              underline
                            />
                          ]}
                        />
                      </p>
                    </Col>
                  </Row>
                </Col>
              </WithRibbon>
            </>
          )}
        </Col>
      </WithRibbon>
    </Modal>
  );
};

ChoiceOfferModal.propTypes = {
  openChoiceOfferModal: PropTypes.bool.isRequired,
  setOpenChoiceOfferModal: PropTypes.func.isRequired,
  productSelected: PropTypes.string.isRequired
};

export default ChoiceOfferModal;
